import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Phone, Award, Users, Shield, Star } from 'lucide-react';

const OFFICE_IMAGE = "https://customer-assets.emergentagent.com/job_handcare-redesign/artifacts/u2wlbpal_banner-pic3.jpg";

export const HeroSection = () => {
  return (
    <section id="home" className="relative overflow-hidden bg-gradient-hero">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-20 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="space-y-6 sm:space-y-8 animate-fade-up order-2 lg:order-1">
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 sm:px-4 py-2 rounded-full">
              <Award className="w-4 sm:w-5 h-4 sm:h-5" />
              <span className="text-xs sm:text-sm font-semibold">Brevard's #1 Hand Specialists</span>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Brevard's #1{' '}
                <span className="text-gradient-primary">Orthopedic Hand</span>{' '}
                Specialists!
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl font-body leading-relaxed">
                Trust your hands to fellowship-trained, board-certified orthopedic surgeons 
                who exclusively treat conditions of the hand, wrist, and elbow.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button variant="hero" size="lg" className="w-full sm:w-auto" asChild>
                <a 
                  href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Book Consultation
                </a>
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto" asChild>
                <a href="tel:321-500-4263" className="flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call 321.500.4263
                </a>
              </Button>
            </div>

            {/* Trust Indicators - Hidden on small mobile, shown on sm+ */}
            <div className="hidden sm:flex flex-wrap gap-4 sm:gap-6 pt-2 sm:pt-4">
              <div className="flex items-center gap-2">
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-full bg-success/10 flex items-center justify-center">
                  <Shield className="w-4 sm:w-5 h-4 sm:h-5 text-success" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-foreground">Board Certified</p>
                  <p className="text-xs text-muted-foreground">Orthopedic Surgery</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-foreground">Fellowship Trained</p>
                  <p className="text-xs text-muted-foreground">Hand & Upper Extremity</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-full bg-warning/10 flex items-center justify-center">
                  <Star className="w-4 sm:w-5 h-4 sm:h-5 text-warning" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-foreground">5-Star Rated</p>
                  <p className="text-xs text-muted-foreground">Google Reviews</p>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-fade-in order-1 lg:order-2" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
              <img 
                src={OFFICE_IMAGE} 
                alt="Melbourne Hand Center Office" 
                className="w-full h-[250px] sm:h-[350px] lg:h-[500px] object-cover"
              />
              {/* Overlay card - Hidden on mobile */}
              <div className="hidden sm:block absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Same-Day Appointments</p>
                    <p className="text-xs text-muted-foreground">Telemedicine Available</p>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-primary/20 border-2 border-card flex items-center justify-center">
                        <Star className="w-4 h-4 text-warning" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-primary text-primary-foreground rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-primary animate-float">
              <p className="text-sm sm:text-lg font-bold leading-tight">BOARD</p>
              <p className="text-sm sm:text-lg font-bold leading-tight">CERTIFIED</p>
            </div>
          </div>
        </div>

        {/* Mobile Trust Indicators */}
        <div className="flex sm:hidden justify-center gap-6 mt-6 pt-6 border-t border-border">
          <div className="text-center">
            <Shield className="w-6 h-6 text-success mx-auto mb-1" />
            <p className="text-xs font-semibold text-foreground">Board Certified</p>
          </div>
          <div className="text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-1" />
            <p className="text-xs font-semibold text-foreground">Fellowship Trained</p>
          </div>
          <div className="text-center">
            <Star className="w-6 h-6 text-warning mx-auto mb-1" />
            <p className="text-xs font-semibold text-foreground">5-Star Rated</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
